import athenaConfig from '../../config';
import AmplitudeUtils from '../../utils/amplitude-utils';
import BasePrimaryAuthCustomizer from './base';
import { getCustomLoginMetadata, processCreds } from '../../utils/authorize-utils';
import { getAuthenticatorMethodType } from '../../utils/widget-facade';
import Footer from '../../components/primary-auth/oie/Footer';
import { isAthenaOneWebOrMobileClient } from '../../utils/app-utils';
import { createWrapper } from '../../utils/react-utils';
import Header, { FontSize } from '../../components/common/Header';
import React from 'react';
import InfoContainer from '../../components/common/InfoContainer';
import { getLocalizedString } from '../../utils/i18n-utils';

const { oieEnabled } = athenaConfig;

export default class PrimaryAuthOieCustomizer extends BasePrimaryAuthCustomizer {
  rememberMeSelector = '#athena-rememberMe-input-field-container';
  allowRegistration = !!settings.allowRegistration;
  headerFontSize = 'large' as FontSize;
  primaryButtonFullWidth = !isAthenaOneWebOrMobileClient();

  async shouldCustomize() {
    return (await super.shouldCustomize()) && oieEnabled;
  }

  async preCustomize() {
    await super.preCustomize();
    if (!isAthenaOneWebOrMobileClient()) {
      this.inputFieldsCustomClass = 'athena-input-form-field fe_u_padding--bottom-large';
    }
  }

  private onIdxProceed() {
    const authClient = this.oktaSignIn.authClient;
    const originalProceed = authClient.idx.proceed.bind(authClient);
    authClient.idx.proceed = async (...args: any) => {
      if (this.context.controller === 'primary-auth' && args[0]?.identifier) {
        await processCreds({ username: args[0].identifier });
      }

      const res = await originalProceed.apply(authClient, args);

      if (args?.length) {
        const currentStep = args[0].step;
        const isVerifyPasswordView =
          currentStep === 'challenge-authenticator' && getAuthenticatorMethodType() === 'password';
        if (args[0].credentials?.passcode && (currentStep === 'identify' || isVerifyPasswordView)) {
          AmplitudeUtils.logCustomEvent('LoginInitiated', getCustomLoginMetadata(this.bannerConfig));
          if (res?.context?.messages?.value?.length) {
            const errorObject = res.context.messages.value[0];
            AmplitudeUtils.logCustomEvent('LoginError', {
              ...errorObject,
              ...getCustomLoginMetadata(this.bannerConfig),
            });
          }
          else {
            AmplitudeUtils.logCustomEvent('LoginSuccess', {
              ...getCustomLoginMetadata(this.bannerConfig),
              userId: res?.context?.user?.value?.id,
            });
          }
        }
        res?.nextStep?.name === 'redirect-idp' &&
          (await AmplitudeUtils.logIdpDiscoveryUserLoginEvent({ isIdpDiscoverySSOUser: true }));
        if (isVerifyPasswordView) {
          await AmplitudeUtils.logIdpDiscoveryUserLoginEvent({ isIdpDiscoverySSOUser: false });
        }
      }
      return res;
    };
  }

  async customize() {
    await super.customize();
    [ 'primary-auth', 'mfa-verify-password' ].includes(this.context.controller) && this.onIdxProceed();
    this.addPrimaryAuthErrorBanner();
  }

  private addPrimaryAuthErrorBanner() {
    if (
      [ 'primary-auth', 'idp-discovery' ].includes(this.context.controller) &&
      this.settings.emailTokenUserRegisteredWarning
    ) {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          title: '',
          description: getLocalizedString('email.token.user.exists.warning'),
          alertType: 'attention',
        },
      });
    }

    if (
      [ 'primary-auth', 'idp-discovery' ].includes(this.context.controller) &&
      this.settings.tempPasswordHint
    ) {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          title: '',
          description: getLocalizedString('temp.password.hint'),
          alertType: 'attention',
        },
      });
    }
  }

  protected getFooter(): React.JSX.Element {
    return (
      <Footer
        footerLinks={jQueryCourage('.js-cancel')}
        forgotPasswordLink={!!(!isAthenaOneWebOrMobileClient() && getAuthenticatorMethodType() === 'password' && this.settings.appType) }
      />
    );
  }

  protected addTitleMessage() {
    if (!isAthenaOneWebOrMobileClient()) {
      const athenaHeaderContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-head"]');
      const container = createWrapper({ hidden: false });
      athenaHeaderContainer.after(container);
      const component = (
        <Header
          headerText={getLocalizedString('primaryauth.titlemessage')}
          fontSize="default"
          fontColor="default"
          fontWeight="regular"
        />
      );
      this.renderReactComponent({
        id: 'okta-form-titlemessage',
        component,
        container,
      });
    }
  }

  protected forgifyController() {
    super.forgifyController();
    if (isAthenaOneWebOrMobileClient()) {
      this.addCopyrightText();
    }
  }
}
